import * as Config from "config";
import useSessionPermissionsLoadState from "permissions/session/use-session-permissions-load-state";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import FilterListIcon from "@mui/icons-material/FilterList";
import UpIcon from "@mui/icons-material/KeyboardArrowUp";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Fab from "@mui/material/Fab";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import Zoom from "@mui/material/Zoom";
import { Theme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import useScrollTrigger from "@mui/material/useScrollTrigger";
import { styled } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import Guid from "_shared/guids/guid";
import { ArrayChip, FilterChipValueType, RangeChip } from "marketplace/_views/filters/filter-chip";
import { cloneFilterChips, FilterChips, FilterChipType } from "marketplace/_views/filters/filter-chips";
import Results from "marketplace/_views/results";
import SearchBar from "marketplace/_views/search-bar";
import SortButton from "marketplace/_views/sort-button";
import MarketplaceSearchRequestSchema from "marketplace/_schema/marketplace-search-request-schema";
import SaveSearchButton from "marketplace/saved-searches/_views/save-search-button";
import {
  MarketplaceSortValue,
  MarketplaceSorts,
  normalizeMarketplaceSort,
  overridesAssistedRanking,
} from "marketplace/search/marketplace-sorts";
import ViewCompanyProfile from "marketplace/companies/profiles/_views/view-company-profile";
import Individual from "marketplace/individuals/individual";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import {
  EntityTypeInclusionFilterNames,
  Filters,
  isEntityTypeInclusionFilterName,
} from "marketplace/search/marketplace-search-filters";
import {
  ParsedFilterChip as ParsedFilterChipType,
  SuppressedInferencesQueryParam,
} from "marketplace/_schema/assisted-search-schema";
import React, { useCallback, useEffect } from "react";
import { useNavigate, useSearchParams, useLoaderData } from "react-router";
import { useSession } from "users/__session/session-context";
import { DialogProps } from "_shared/_views/dialogs/dialog";
import FilterList from "marketplace/_views/filters/filter-list";
import ViewTeamProfile from "marketplace/team-profile/_views/view-team-profile";
import { useSelector } from "react-redux";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { shellTopBannersHeight } from "_shared/_views/shell-top-banners";
import { shellBottomBarHeight } from "_shared/_views/shell-bottom-bar";

/**
 * The widest the results column grows. Past it a card's three columns of facts drift apart and the
 * search bar runs on across the window; the column stops here and the search bar ends where it does.
 */
const RESULTS_MAX_WIDTH = 1200;

/** Wide enough for the longest filter title, "Professional Registration #", on one line. */
const FILTER_PANEL_SPACING = 36;

const PageContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  height: `calc(100vh - ${Config.elementHeight.appBar}px - ${Config.elementHeight.footer}px - ${shellTopBannersHeight()} - ${shellBottomBarHeight()})`,
}));
const SearchContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  backgroundColor: "antiquewhite",
  borderRadius: theme.spacing(0.5),
  paddingBottom: theme.spacing(2),
  paddingLeft: theme.spacing(3.5),
  paddingRight: theme.spacing(3.5),
  paddingTop: theme.spacing(2),
  position: "sticky",
  width: "100%",
  zIndex: 10,
  // The bar ends at the results column's right edge: the column starts past the filter panel and the
  // pane's gutter, and this container's own padding already sits inside that.
  "& > *": {
    maxWidth: `calc(${theme.spacing(2)} + ${RESULTS_MAX_WIDTH}px - ${theme.spacing(3.5)})`,
  },
  [theme.breakpoints.up("xl")]: {
    "& > *": {
      maxWidth: `calc(${theme.spacing(FILTER_PANEL_SPACING)} + ${theme.spacing(1)} + ${RESULTS_MAX_WIDTH}px - ${theme.spacing(3.5)})`,
    },
  },
}));
const MainContent = styled("div")(() => ({
  display: "flex",
  flex: 1,
}));
const ResultsContainer = styled("div")(({ theme }) => ({
  position: "relative",
  width: "100%",
  display: "grid",
  gridTemplateColumns: "1fr",
  [theme.breakpoints.down("xl")]: {
    marginTop: 0,
  },
}));
const ResultsPane = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("xl")]: {
    overflowX: "hidden",
    overflowY: "visible",
    // No filter panel to the left below xl, so the cards keep a gutter of their own.
    padding: theme.spacing(0, 1, 0, 2),
  },
  backgroundColor: "#FAFAFA",
  flex: 1,
  height: "100%",
  maxHeight: `calc(100vh - ${Config.elementHeight.appBar}px - ${Config.elementHeight.marketplaceSearchBar}px - ${Config.elementHeight.footer}px - ${shellTopBannersHeight()} - ${shellBottomBarHeight()})`,
  overflowY: "auto",
  padding: theme.spacing(0, 1),
  position: "relative",
  "& > *": {
    maxWidth: RESULTS_MAX_WIDTH,
  },
}));
const SearchFilterChip = styled(Chip)(() => ({
  flexShrink: 0,
}));
// Visually distinct from a filter the user set themselves. A chip the software chose and a chip the
// user chose should not look alike, because only one of them might be wrong.
const ParsedFilterChip = styled(Chip)(() => ({
  flexShrink: 0,
  backgroundColor: "rgba(225, 153, 65, 0.18)",
  borderColor: "#E19941",
  borderStyle: "solid",
  borderWidth: 1,
  "& .MuiChip-icon": {
    color: "#C9853A",
  },
}));
/**
 * The count on the left and what acts on the results, Filters and Sort, on the right; under them,
 * one row of what is narrowing them. On a phone that row scrolls sideways rather than wrapping, so
 * it stays one line high above the first result.
 */
const TopBar = styled("div")(({ theme }) => ({
  backdropFilter: "blur(7px) saturate(200%)",
  backgroundColor: "rgba(250, 250, 250, 0.87)",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
  padding: theme.spacing(1, 0),
  position: "sticky",
  top: 0,
  zIndex: 10,
}));
const ToolbarRow = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
  justifyContent: "space-between",
  minHeight: 42,
}));
const ResultCount = styled(Typography)(() => ({
  whiteSpace: "nowrap",
}));
const ToolbarActions = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexShrink: 0,
  gap: theme.spacing(0.5),
}));
const ChipRow = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    flexWrap: "nowrap",
    overflowX: "auto",
    paddingBottom: theme.spacing(0.5),
    scrollbarWidth: "thin",
  },
}));
const FilterListComponent = styled(FilterList)(() => ({
  position: "sticky",
  top: Config.elementHeight.appBar + Config.elementHeight.marketplaceSearchBar,
}));
const FilterContainer = styled("div")(({ theme }) => ({
  backgroundColor: "#FAFAFA",
  maxHeight: `calc(100vh - ${Config.elementHeight.appBar}px - ${Config.elementHeight.marketplaceSearchBar}px - ${Config.elementHeight.footer}px - ${shellTopBannersHeight()} - ${shellBottomBarHeight()})`,
  flexShrink: 0,
  width: theme.spacing(FILTER_PANEL_SPACING),
  overflowX: "hidden",
  overflowY: "auto",
}));
const BackToTopButton = styled(Fab)(({ theme }) => ({
  [theme.breakpoints.down("xl")]: {
    right: theme.spacing(1),
  },
  position: "sticky",
  alignSelf: "center",
  justifySelf: "center",
  // The results pane scrolls, so it clips whatever reaches past its bottom edge. A Fab's shadow
  // reaches 19px below the button; at 8px or 12px from the edge, its lower part was cut off.
  bottom: theme.spacing(3),
}));
const ShowResultsButton = styled(Button)(() => ({
  display: "flex",
  margin: "auto",
}));

const supportedMarketplaceCategoryIds = new Set(
  [...Config.AllCategories, ...Config.MarketplaceCategories].map((category) => category.id),
);

function normalizeMarketplaceCategory(category: string | null | undefined): string {
  return category && supportedMarketplaceCategoryIds.has(category) ? category : "all";
}

function normalizeEntityTypeSearchParams(params: URLSearchParams): boolean {
  let changed = false;

  for (const filterName of EntityTypeInclusionFilterNames) {
    if (params.has(filterName) && params.get(filterName) !== "false") {
      params.delete(filterName);
      changed = true;
    }
  }
  return changed;
}

/**
 * Whether a filter control reported that its value was removed, rather than changed.
 *
 * `false` is deliberately not cleared: a switch turned off is a removal, and the filter list already
 * reports that separately. Zero is a value a numeric filter can legitimately be set to.
 */
function isClearedFilterValue(value: FilterChipValueType | undefined): value is null | undefined | "" | [] {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;
  return value === "";
}

function toMarketplaceSearchFilters(filters: FilterChips): Record<string, string> {
  const searchFilters = filters.toDictionary();
  for (const filterName of EntityTypeInclusionFilterNames) {
    if (filters.findByName(filterName)?.enabled) {
      delete searchFilters[filterName];
    } else {
      searchFilters[filterName] = "false";
    }
  }
  return searchFilters;
}

type MarketplaceProps = {
  embedded?: boolean;
  selectingLeader?: boolean;
  selectingMember?: boolean;
  onTeamMemberSelected?: (teamMember: Individual) => void;
};

export default function MarketplacePage(props: Readonly<MarketplaceProps>) {
  const { embedded, selectingLeader, selectingMember, onTeamMemberSelected } = props;

  const [scrollTarget, setScrollTarget] = React.useState<Element | undefined>(undefined);
  const resultsNodeRef = React.useRef<HTMLDivElement | null>(null);
  const resultsRef = useCallback((node: HTMLDivElement | null) => {
    resultsNodeRef.current = node;
    setScrollTarget(node ?? undefined);
  }, []);
  const createInitialFilters = () => {
    const initialFilters = new FilterChips(cloneFilterChips(Filters));
    return initialFilters;
  };

  const filtersRef = React.useRef<FilterChips>(createInitialFilters());

  const [searchTerm, setSearchTerm] = React.useState("");
  const [parsedFilters, setParsedFilters] = React.useState<ParsedFilterChipType[]>([]);
  const [filters, setFilters] = React.useState<FilterChips>(() => new FilterChips(cloneFilterChips(Filters)));
  const [sortBy, setSortBy] = React.useState<MarketplaceSortValue>(
    MarketplaceSorts.Relevance,
  );
  const [category, setCategory] = React.useState("all");
  // Undefined until a search lands, so the toolbar never reads "0 Results" before the first one.
  const [numResults, setNumResults] = React.useState<number | undefined>(undefined);
  const [filterPopoverAnchor, setFilterPopoverAnchor] = React.useState<HTMLButtonElement | null>(null);
  const [filterDialogId, setFilterDialogId] = React.useState<Guid | null>(null);
  const dialogOpen = React.useRef(false);

  const { openDialog, closeDialog, updateDialogProps } = useDialog();

  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const xlDown = useMediaQuery((theme: Theme) => theme.breakpoints.down("xl"));
  const xlUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("xl"));

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const parsedSearchParams = React.useMemo(() => new URLSearchParams(searchParamsKey), [searchParamsKey]);
  // Derived rather than held in state: the query string already carries it, so duplicating it would
  // give two sources of truth that can disagree. It also means an AI search survives a reload and
  // can be shared as a link.
  const aiMode =
    searchParams.get("aiMode") === "true" &&
    normalizeMarketplaceCategory(searchParams.get("category")) === "all";
  const routeParams = useLoaderData();
  const session = useSession();
  const legalEntityIdValue = session.user?.legalEntityId?.value;

  const sessionPermissions = useSelector(getSessionPermissions);
  const permissionsLoadState = useSessionPermissionsLoadState();

  const scrollTrigger = useScrollTrigger({
    target: scrollTarget,
    disableHysteresis: true,
    threshold: 100,
  });
  const markResultsPending = useCallback(() => setNumResults(undefined), []);

  /**
   * Publishes the current filters to the sidebar and the chip bar.
   *
   * A copy, not the working set. Filter chips are mutable objects that the handlers below change in
   * place, and handing React the same instances back leaves a memoized render free to conclude
   * nothing changed - which is exactly what happened to a filter whose switch was thrown but whose
   * value never reached the query string: the results were right and the sidebar was not.
   */
  const publishFilters = useCallback(
    () => setFilters(new FilterChips(cloneFilterChips(filtersRef.current.chips))),
    [],
  );

  // Helper function to parse query string and update filters
  function parseQueryString(
    searchParams: URLSearchParams,
    setSearchTerm: (term: string) => void,
    setCategory: (cat: string) => void,
    setSortBy: (sort: MarketplaceSortValue) => void,
    setFilters: (chips: FilterChips) => void,
  ) {
    const nextFilters = new FilterChips(cloneFilterChips(Filters));

    if (searchParams.has("query")) {
      setSearchTerm(searchParams.get("query") ?? "");
    } else {
      setSearchTerm("");
    }

    if (searchParams.has("query")) setSearchTerm(searchParams.get("query") ?? "");
    setCategory(normalizeMarketplaceCategory(searchParams.get("category")));
    setSortBy(normalizeMarketplaceSort(searchParams.get("sortBy")));

    for (const [filterName, filterValue] of searchParams.entries()) {
      const targetChip = nextFilters.findByName(filterName);
      if (!targetChip) continue;

      if (filterName === "query" && filterValue === "") {
        nextFilters.disable(filterName);
        continue;
      }

      if (isEntityTypeInclusionFilterName(filterName) && filterValue === "false") {
        nextFilters.disable(filterName);
        continue;
      }

      if (filterValue === "") {
        nextFilters.disable(filterName);
      } else {
        nextFilters.updateValue(filterName, filterValue.split(","));
      }
    }

    filtersRef.current = nextFilters;
    setFilters(nextFilters);
  }

  // Helper function to load route profile and open dialog
  function loadRouteProfile(
    type: string | undefined,
    id: string | undefined,
    openDialog: (props: DialogProps, onClose?: () => void) => void,
    navigate: (path: string) => void,
    returnUrl: string,
  ) {
    if (!type || !id) return;

    const guidId = new Guid(id);

    if (type === "individual") {
      openDialog({
        component: <ViewIndividualProfile individualId={guidId} />,
        MuiProps: {
          fullWidth: true,
          maxWidth: "lg",
        },
        contentSxProps: {
          display: "flex",
        },
        titleStyle: {
          paddingBottom: 0,
        },
        onClose: () => {
          dialogOpen.current = false;
          navigate(returnUrl);
        },
      });
    } else if (type === "team") {
      openDialog({
        component: <ViewTeamProfile teamId={guidId} />,
        MuiProps: {
          fullWidth: true,
          maxWidth: "lg",
        },
        contentSxProps: {
          display: "flex",
        },
        titleStyle: {
          paddingBottom: 0,
        },
        onClose: () => {
          dialogOpen.current = false;
          navigate(returnUrl);
        },
      });
    } else if (type === "company") {
      openDialog({
        component: <ViewCompanyProfile companyId={guidId} />,
        MuiProps: {
          fullWidth: true,
          maxWidth: "lg",
        },
        contentSxProps: {
          display: "flex",
        },
        titleStyle: {
          paddingBottom: 0,
        },
        onClose: () => {
          dialogOpen.current = false;
          navigate(returnUrl);
        },
      });
    }
  }

  /**
   * Applies one change to the query string without disturbing anything else in it.
   *
   * Every one of these handlers used to copy the `searchParams` its render closed over, edit one key
   * and write the whole thing back. Two changes in quick succession then raced: the second handler
   * could still be holding the snapshot from before the first, so writing it back *resurrected* the
   * filter the first change had just removed. A person toggling two filters quickly saw one they had
   * switched off come back, and with it results that no longer matched the sidebar.
   *
   * React Router's updater form hands us the query string as it is at the moment of the write, so a
   * change can only ever affect the key it names. The side effects around these calls deliberately
   * stay outside the updater: React may invoke an updater more than once.
   */
const updateSearchParams = useCallback(
  (apply: (params: URLSearchParams) => void) => {
    let changed = false;
    setSearchParams((previous) => {
      const params = new URLSearchParams(previous);
      apply(params);
      changed = params.toString() !== previous.toString();
      return changed ? params : previous;
    });
    return changed;
  },
  [setSearchParams],
);

  const handleFilterChanged = useCallback(
    (updatedFilter: FilterChipType) => {
      // A switch reports itself here as well as through its removal callback when it is turned off,
      // and that callback owns the removal. Handling it twice would write the query string twice.
      if (updatedFilter.value === false) return;

      const targetChip = filtersRef.current.findByName(updatedFilter.filterName);

      if (!targetChip) return;

      if (isEntityTypeInclusionFilterName(updatedFilter.filterName)) {
        filtersRef.current.updateValue(updatedFilter.filterName, "true");
        publishFilters();
        markResultsPending();
        updateSearchParams((params) => {
          params.delete(updatedFilter.filterName);
          params.set("pageIndex", "0");
        });
        return;
      }

      // The control was emptied rather than switched off. Leaving the previous value in the query
      // string kept the results narrowed by a value the sidebar no longer showed - a filter nothing
      // on screen could explain, and no way to undo except by switching the whole filter off.
      // Resetting through the chip's own disable/enable clears its stored value and its summary
      // label while leaving the control open for a new one.
      if (isClearedFilterValue(updatedFilter.value)) {
        targetChip.disable();
        targetChip.enable();
        publishFilters();
        // Revealing a filter that has no value yet changes nothing about the search, and must not
        // touch the query string: the enabled controls are rebuilt from it, so a write here would
        // immediately fold the control the user just opened.
        if (!searchParams.has(updatedFilter.filterName)) return;

        markResultsPending();
        updateSearchParams((params) => {
          params.delete(updatedFilter.filterName);
          params.set("pageIndex", "0");
        });
        return;
      }

      let filterValue = "";
      if (Array.isArray(updatedFilter.value)) {
        filterValue = updatedFilter.value.join(",");
      } else {
        filterValue = updatedFilter.value.toString();
      }

      if (targetChip instanceof ArrayChip || targetChip instanceof RangeChip) {
        filtersRef.current.updateValue(updatedFilter.filterName, updatedFilter.value as string[]);
      } else {
        filtersRef.current.updateValue(updatedFilter.filterName, filterValue);
      }

      publishFilters();
      markResultsPending();

      updateSearchParams((params) => {
        params.set(updatedFilter.filterName, filterValue);
        params.set("pageIndex", "0");
      });
    },
    [markResultsPending, publishFilters, searchParams, updateSearchParams],
  );

  const handleFilterDisabled = useCallback(
    (updatedFilter: FilterChipType) => {
      filtersRef.current.disable(updatedFilter.filterName);
      publishFilters();
      markResultsPending();

      if (updatedFilter.filterName === "query") {
        setSearchTerm("");
        setParsedFilters([]);
      }

      updateSearchParams((params) => {
        if (isEntityTypeInclusionFilterName(updatedFilter.filterName)) {
          params.set(updatedFilter.filterName, "false");
        } else {
          params.delete(updatedFilter.filterName);
        }
        params.set("pageIndex", "0");
        if (updatedFilter.filterName === "query") params.delete(SuppressedInferencesQueryParam);
      });
    },
    [markResultsPending, publishFilters, updateSearchParams],
  );

  useEffect(() => {
    if (!legalEntityIdValue) return;

    const normalizedParams = new URLSearchParams(parsedSearchParams);
    const requestedSort = normalizedParams.get("sortBy");
    const normalizedSort = normalizeMarketplaceSort(requestedSort);
    const requestedCategory = normalizedParams.get("category");
    const normalizedCategory = normalizeMarketplaceCategory(requestedCategory);
    let replaceSearchParams = false;

    if (normalizeEntityTypeSearchParams(normalizedParams)) {
      replaceSearchParams = true;
    }

    if (requestedSort && requestedSort !== normalizedSort) {
      normalizedParams.set("sortBy", normalizedSort);
      normalizedParams.set("pageIndex", "0");
      replaceSearchParams = true;
    }

    if (requestedCategory !== null && requestedCategory !== normalizedCategory) {
      normalizedParams.set("category", normalizedCategory);
      normalizedParams.set("pageIndex", "0");
      replaceSearchParams = true;
    }

    if (normalizedCategory !== "all") {
      if (normalizedParams.has("aiMode") || normalizedParams.has(SuppressedInferencesQueryParam)) {
        replaceSearchParams = true;
      }
      normalizedParams.delete("aiMode");
      normalizedParams.delete(SuppressedInferencesQueryParam);
    }

    if (replaceSearchParams) setSearchParams(normalizedParams, { replace: true });

    parseQueryString(
      normalizedParams,
      setSearchTerm,
      setCategory,
      setSortBy,
      setFilters,
    );
  }, [legalEntityIdValue, parsedSearchParams, setSearchParams]);

  useEffect(() => {
    if (dialogOpen.current) return;
    if (!session.user?.legalEntityId.value) return;

    if (!routeParams?.pageTab || !routeParams?.id) return;
    const type = routeParams.pageTab;
    const id = routeParams.id;

    dialogOpen.current = true;
    const returnUrl = searchParamsKey ? `/marketplace?${searchParamsKey}` : "/marketplace";
    loadRouteProfile(type, id, openDialog, navigate, returnUrl);
  }, [
    navigate,
    openDialog,
    routeParams?.pageTab,
    routeParams?.id,
    searchParamsKey,
    session.user?.legalEntityId.value,
  ]);

  // A left-out type's chip puts the type back, the same as turning its switch on again.
  const handleEntityTypeRestored = useCallback(
    (filterName: string) => {
      filtersRef.current.enable(filterName);
      filtersRef.current.updateValue(filterName, "true");
      publishFilters();
      markResultsPending();
      updateSearchParams((params) => {
        params.delete(filterName);
        params.set("pageIndex", "0");
      });
    },
    [markResultsPending, publishFilters, updateSearchParams],
  );

  // Every type back in and every other filter off, in one write: several writes in one tick would
  // each start from the same query string, and all but the last would be lost.
  const handleFiltersCleared = useCallback(() => {
    const cleared: string[] = [];
    for (const chip of filtersRef.current.chips) {
      if (isEntityTypeInclusionFilterName(chip.filterName)) {
        if (chip.enabled) continue;
        chip.enable();
        filtersRef.current.updateValue(chip.filterName, "true");
        cleared.push(chip.filterName);
      } else if (chip.enabled && chip.filterName !== "query") {
        chip.disable();
        cleared.push(chip.filterName);
      }
    }
    if (cleared.length === 0) return;

    publishFilters();
    markResultsPending();
    updateSearchParams((params) => {
      for (const filterName of cleared) params.delete(filterName);
      params.set("pageIndex", "0");
    });
  }, [markResultsPending, publishFilters, updateSearchParams]);

  const handleFilterEnabled = useCallback(
    (updatedFilter: FilterChipType) => {
      filtersRef.current.enable(updatedFilter.filterName);
      publishFilters();
    },
    [publishFilters],
  );

  function handleCategoryChanged(category: Config.Category) {
    markResultsPending();
    setSearchTerm("");
    setCategory(category.id);
    setSortBy(MarketplaceSorts.Relevance);

    filtersRef.current = new FilterChips(cloneFilterChips(Filters));
    publishFilters();
    setParsedFilters([]);

    const params = new URLSearchParams();
    params.set("category", category.id);
    params.set("pageIndex", "0");
    setSearchParams(params);
  }

  function submitSearch(term: string, useAi: boolean) {
    markResultsPending();
    setSearchTerm(term);
    setParsedFilters([]);
    filtersRef.current.updateValue("query", term);
    filtersRef.current.enable("query");
    publishFilters();

    const params = new URLSearchParams(searchParams);
    params.set("query", term);
    params.set("pageIndex", "0");
    params.delete(SuppressedInferencesQueryParam);
    if (useAi && category === "all") {
      params.set("aiMode", "true");
      // An assisted search ranks its own results, and the API applies an explicit sort on top of
      // that ranking - discarding it. A sort picked for some earlier search would otherwise keep
      // outranking every later AI search, with nothing on screen saying why the ordering ignored
      // the question that was asked.
      if (overridesAssistedRanking(sortBy)) {
        params.set("sortBy", MarketplaceSorts.Relevance);
        setSortBy(MarketplaceSorts.Relevance);
      }
    } else {
      params.delete("aiMode");
    }
    setSearchParams(params);
  }

  function handleSearch(term: string = "") {
    submitSearch(term, false);
  }

  function handleAiSearch(term: string) {
    if (category !== "all") return;
    submitSearch(term, true);
  }

  function handleSort(sortField: MarketplaceSortValue) {
    const normalizedSort = normalizeMarketplaceSort(sortField);
    markResultsPending();
    setSortBy(normalizedSort);
    const params = new URLSearchParams(searchParams);
    params.set("sortBy", normalizedSort);
    params.set("pageIndex", "0");
    setSearchParams(params);
  }

  const closeFilterDialog = useCallback(() => {
    setFilterDialogId(null);
    closeDialog();
  }, [closeDialog]);

  const buildFilterDialogProps = useCallback(
    (): DialogProps => ({
      MuiProps: {
        fullScreen: true,
        keepMounted: true,
        disablePortal: true,
      },
      onClose: () => setFilterDialogId(null),
      title: (
        <ShowResultsButton
          size="large"
          variant="contained"
          disableElevation={true}
          color="primary"
          disabled={numResults === undefined}
          startIcon={numResults === undefined ? <CircularProgress color="inherit" size={18} /> : undefined}
          onClick={closeFilterDialog}
        >
          {numResults === undefined ? "Updating Results..." : `Show ${numResults} Result${numResults === 1 ? "" : "s"}`}
        </ShowResultsButton>
      ),
      component: (
        <FilterList
          dialog={true}
          filters={filters}
          searchParams={searchParams}
          onChange={handleFilterChanged}
          onAdded={handleFilterEnabled}
          onRemoved={handleFilterDisabled}
        />
      ),
    }),
    [
      closeFilterDialog,
      filters,
      handleFilterChanged,
      handleFilterDisabled,
      handleFilterEnabled,
      numResults,
      searchParams,
    ],
  );

  useEffect(() => {
    if (!filterDialogId) return;

    updateDialogProps(filterDialogId, buildFilterDialogProps());
  }, [buildFilterDialogProps, filterDialogId, updateDialogProps]);

  function openFilterDialog() {
    const dialogId = openDialog(buildFilterDialogProps());
    setFilterDialogId(dialogId);
  }

  // Absent is not the same as refused. Reading the flag off an undefined permissions object made a
  // reader who was merely waiting look like one who had been denied.
  if (!sessionPermissions) return permissionsLoadState;
  if (!sessionPermissions.includesAccessToMarketplaceSearch) {
    return <NotAuthorized />;
  }

  const hasSelectedEntityType = EntityTypeInclusionFilterNames.some(
    (filterName) => filters.findByName(filterName)?.enabled,
  );
  const excludedTypeChips = filters.chips.filter(
    (chip) => isEntityTypeInclusionFilterName(chip.filterName) && !chip.enabled,
  );
  const appliedChips = filters.chips.filter(
    (chip) => !isEntityTypeInclusionFilterName(chip.filterName) && chip.enabled,
  );
  // What narrows the results. The search text is not a filter; it has the search bar.
  const activeFilterCount =
    excludedTypeChips.length + appliedChips.filter((chip) => chip.filterName !== "query").length;
  const hasChips =
    excludedTypeChips.length > 0 ||
    appliedChips.length > 0 ||
    !hasSelectedEntityType ||
    (aiMode && parsedFilters.length > 0);

  return (
    <PageContainer>
      <SearchContainer>
        <SearchBar
          searchTerm={searchTerm}
          searchParams={searchParams}
          aiModeAvailable={category === "all"}
          onAiSearch={handleAiSearch}
          onCategoryChanged={handleCategoryChanged}
          onSearch={handleSearch}
          onSearchCleared={() => {
            const queryChip = filtersRef.current.findByName("query");
            if (queryChip) {
              handleFilterDisabled(queryChip);
            }
          }}
        />
      </SearchContainer>
      <MainContent>
        {xlUp && (
          <FilterContainer>
            <FilterListComponent
              filters={filters}
              dialog={false}
              searchParams={searchParams}
              onChange={handleFilterChanged}
              onAdded={handleFilterEnabled}
              onRemoved={handleFilterDisabled}
            />
          </FilterContainer>
        )}
        <ResultsPane ref={resultsRef}>
          <TopBar>
            <ToolbarRow>
              <ResultCount aria-live="polite" variant="h6">
                {numResults !== undefined && `${numResults} Result${numResults === 1 ? "" : "s"}`}
              </ResultCount>
              <ToolbarActions>
                {xlDown && (
                  <>
                    <Button
                      size="large"
                      color="primary"
                      startIcon={<FilterListIcon />}
                      onClick={(event) => (mdUp ? setFilterPopoverAnchor(event.currentTarget) : openFilterDialog())}
                    >
                      Filters
                      {activeFilterCount > 0 && (
                        <>
                          <span aria-hidden="true">&nbsp;· {activeFilterCount}</span>
                          <span style={visuallyHidden}>, {activeFilterCount} active</span>
                        </>
                      )}
                    </Button>
                    <Popover
                      anchorEl={filterPopoverAnchor}
                      open={Boolean(filterPopoverAnchor)}
                      onClose={() => setFilterPopoverAnchor(null)}
                    >
                      <FilterList
                        dialog={true}
                        filters={filters}
                        searchParams={searchParams}
                        onChange={handleFilterChanged}
                        onAdded={handleFilterEnabled}
                        onRemoved={handleFilterDisabled}
                      />
                    </Popover>
                  </>
                )}
                {/* An assisted search is saved later as the filters it resolves to; until then only an
                    ordinary search can be kept (#2411). */}
                {!embedded && !aiMode && (
                  <SaveSearchButton
                    request={MarketplaceSearchRequestSchema.deserialize({
                      ...toMarketplaceSearchFilters(filters),
                      ...(searchTerm ? { query: searchTerm } : {}),
                      category,
                      sortBy,
                    })}
                    suggestedName={searchTerm.trim() || "Marketplace search"}
                  />
                )}
                <SortButton sortBy={sortBy} onSort={handleSort} />
              </ToolbarActions>
            </ToolbarRow>
            {hasChips && (
              <ChipRow>
                {aiMode &&
                  parsedFilters.map((parsed) => (
                    <ParsedFilterChip
                      key={parsed.filterName}
                      label={parsed.label}
                      size={mdDown ? "small" : "medium"}
                      icon={<AutoAwesomeIcon fontSize="small" />}
                      onDelete={() => {
                        const params = new URLSearchParams(searchParams);
                        const suppressed = new Set(
                          params
                            .getAll(SuppressedInferencesQueryParam)
                            .flatMap((value) => value.split(","))
                            .filter(Boolean),
                        );
                        suppressed.add(parsed.filterName);
                        params.set(SuppressedInferencesQueryParam, [...suppressed].join(","));
                        params.set("pageIndex", "0");
                        setSearchParams(params);
                        setParsedFilters((current) =>
                          current.filter((chip) => chip.filterName !== parsed.filterName),
                        );
                      }}
                    />
                  ))}
                {!hasSelectedEntityType && (
                  <SearchFilterChip label="No Profile Types Selected" disabled size={mdDown ? "small" : "medium"} />
                )}
                {/*
                  A type is shown only when it is left out. All three included is no filter at all, and
                  drawing it as three chips read as three restrictions the searcher never chose.
                */}
                {excludedTypeChips.map((chip) => (
                  <SearchFilterChip
                    key={chip.filterName}
                    label={`${chip.title}: No`}
                    size={mdDown ? "small" : "medium"}
                    onDelete={() => handleEntityTypeRestored(chip.filterName)}
                  />
                ))}
                {appliedChips.map((chip: FilterChipType) => (
                  <SearchFilterChip
                    key={chip.filterName}
                    label={chip.label}
                    size={mdDown ? "small" : "medium"}
                    onDelete={() => handleFilterDisabled(chip)}
                  />
                ))}
                {activeFilterCount > 1 && (
                  <Button size="small" onClick={handleFiltersCleared} sx={{ flexShrink: 0 }}>
                    Clear all
                  </Button>
                )}
              </ChipRow>
            )}
          </TopBar>
          <ResultsContainer>
            <Results
              session={session}
              embedded={embedded}
              hideResultCount
              selectingTeamLeader={selectingLeader}
              selectingTeamMember={selectingMember}
              category={category}
              aiMode={aiMode}
              onParsedFiltersApplied={setParsedFilters}
              searchTerm={searchTerm}
              filters={toMarketplaceSearchFilters(filters)}
              sortBy={sortBy}
              navigate={navigate}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              onResults={(results: number | undefined) => setNumResults(results ?? 0)}
              onTeamMemberSelected={onTeamMemberSelected}
            />
            <Zoom in={scrollTrigger}>
              <BackToTopButton
                color="primary"
                size="medium"
                variant="extended"
                onClick={() => {
                  if (resultsNodeRef.current) resultsNodeRef.current.scrollTop = 0;
                }}
              >
                <UpIcon />
                Back To Top
              </BackToTopButton>
            </Zoom>
          </ResultsContainer>
        </ResultsPane>
      </MainContent>
    </PageContainer>
  );
}
